import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import * as luxon from 'luxon';

export interface CalendarParams {
  dateTo?: string;
  dateFrom?: string;
  timeSet?: string;
}

export interface UseCalendarParamsOptions {
  /**
   * Current selected date range from calendar state
   */
  selectedDate: [Date, Date];
  
  /**
   * Calendar Redux action creator for setting hive date
   */
  calendarAction: any;
  
  /**
   * Whether to automatically process URL parameters on mount
   * @default true
   */
  enabled?: boolean;
  
  /**
   * Custom URL search params (for testing or custom scenarios)
   * @default window.location.search
   */
  searchParams?: string;
}

export interface UseCalendarParamsReturn {
  /**
   * Manually process calendar parameters from URL
   */
  processCalendarParams: () => void;
  
  /**
   * Whether calendar parameters are currently being processed
   */
  isProcessing: boolean;
  
  /**
   * Last processed calendar parameters
   */
  lastProcessedParams: CalendarParams | null;
}

/**
 * Helper hook to clamp the hive calendar selection within sites availability range
 * Call this once at app level in each admin app (admin-kwick, admin-hive, admin-eon)
 */
export function useClampCalendarToSitesRange(options: {
  selectedDate: [Date, Date];
  calendarAction: any;
}): void {
  const dispatch = useDispatch();
  const metadata = useSelector((state: any) => state.metadata);
  const calendar = useSelector((state: any) => state.calendar);
  const selectedTimeSet: string | undefined = calendar?.hiveCalendar?.selectedTimeSet;
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  const getContext = useCallback((): 'site' | 'community' | 'member' | null => {
    try {
      // Prefer Redux metadata entityType if available
      const entityType = (metadata as any)?.entityType as string | null;
      if (entityType === 'site' || entityType === 'community' || entityType === 'member') {
        return entityType as any;
      }
      // Fallback to pathname parsing
      const seg = (pathname.split('/')[1] || '').toLowerCase();
      if (seg.includes('site') || seg.includes('household')) return 'site';
      if (seg.includes('community')) return 'community';
      if (seg.includes('member')) return 'member';
      return null;
    } catch {
      return null;
    }
  }, [metadata?.entityType, pathname]);

  const getSitesForContext = useCallback(() => {
    const allSites = (metadata?.entities as any)?.sites || (metadata?.entities as any)?.site || [];
    const idFromState = (metadata as any)?.selectedEntityId || null;
    const idFromPath = (pathname.split('/')[2] || null) as string | null;
    const id = idFromState || idFromPath;
    const ctx = getContext();
    if (!ctx || !id || !Array.isArray(allSites)) return [];
    switch (ctx) {
      case 'site':
        return allSites.filter((s: any) => s?.siteId === id);
      case 'community':
        return allSites.filter((s: any) => s?.communityId === id);
      case 'member':
        return allSites.filter((s: any) => s?.memberId === id);
      default:
        return [];
    }
  }, [metadata?.entities, metadata?.selectedEntityId, pathname, getContext]);

  const safeDate = (str?: string): Date | null => {
    try {
      return str ? new Date(str) : null;
    } catch {
      return null;
    }
  };

  const getSiteRange = (site: any): { start: Date | null; end: Date | null } => {
    const start = safeDate(site?.startDate) || safeDate(site?.joinDate) || null;
    const end = safeDate(site?.endDate) || safeDate(site?.leaveDate) || null;
    return { start, end };
  };

  const getGlobalRange = useCallback((sites: any[]) => {
    const starts: number[] = [];
    const ends: number[] = [];
    sites.forEach((s) => {
      const { start, end } = getSiteRange(s);
      if (start instanceof Date && !isNaN(start.getTime())) starts.push(start.getTime());
      if (end instanceof Date && !isNaN(end.getTime())) ends.push(end.getTime());
    });
    if (!starts.length || !ends.length) return null;
    return { minStart: new Date(Math.min(...starts)), maxEnd: new Date(Math.max(...ends)) } as const;
  }, []);

  const getWindowDays = (timeSet: string | undefined, fallbackDays: number): number => {
    switch (timeSet) {
      case 'day':
        return 1;
      case 'week':
        return 7;
      case 'month':
        return 30;
      case 'year':
        return 365;
      default:
        return Math.max(1, fallbackDays || 1);
    }
  };

  // Clamp selectedDate within global sites range for current context
  useEffect(() => {
    try {
      const [from, to] = options.selectedDate || [] as any;
      if (!(from instanceof Date) || !(to instanceof Date)) return;

      const sites = getSitesForContext();
      if (!sites?.length) return;
      const range = getGlobalRange(sites);
      if (!range) return;

      const fromMs = from.getTime();
      const toMs = to.getTime();
      if (isNaN(fromMs) || isNaN(toMs)) return;

      const widthDaysCurrent = Math.max(1, Math.round((toMs - fromMs) / 86400000));
      const wantDays = getWindowDays(selectedTimeSet, widthDaysCurrent);

      let newFrom = new Date(fromMs);
      let newTo = new Date(toMs);

      const minStartMs = range.minStart.getTime();
      const maxEndMs = range.maxEnd.getTime();

      if (toMs < minStartMs) {
        newFrom = new Date(minStartMs);
        newTo = new Date(minStartMs + wantDays * 86400000);
      } else if (fromMs > maxEndMs) {
        newTo = new Date(maxEndMs);
        newFrom = new Date(maxEndMs - wantDays * 86400000);
      } else {
        if (fromMs < minStartMs) newFrom = new Date(minStartMs);
        if (toMs > maxEndMs) newTo = new Date(maxEndMs);
        if (newTo.getTime() - newFrom.getTime() < wantDays * 86400000) {
          const candidateTo = new Date(newFrom.getTime() + wantDays * 86400000);
          newTo = new Date(Math.min(candidateTo.getTime(), maxEndMs));
        }
      }

      const changed = newFrom.getTime() !== fromMs || newTo.getTime() !== toMs;
      if (changed) {
        dispatch(
          options.calendarAction.setHiveDate({
            timeSet: selectedTimeSet || 'day',
            date: [newFrom, newTo],
            setQueryParams: true,
          })
        );
      }
    } catch {
      // no-op
    }
  }, [options.selectedDate, metadata?.entities, metadata?.selectedEntityId, metadata?.entityType, pathname, selectedTimeSet, dispatch, getSitesForContext, getGlobalRange]);
}

/**
 * Custom hook to handle calendar URL parameters parsing and processing
 * 
 * This hook encapsulates the complex logic for:
 * - Parsing URL parameters for calendar dates
 * - Validating date formats and ranges
 * - Comparing with current selected dates using Luxon
 * - Dispatching Redux actions for calendar updates
 * 
 * @param options - Configuration options for calendar parameter processing
 * @returns Calendar parameter processing state and controls
 */
export function useCalendarParams(options: UseCalendarParamsOptions): UseCalendarParamsReturn {
  const {
    selectedDate,
    calendarAction,
    enabled = true,
    searchParams = window.location.search,
  } = options;

  const dispatch = useDispatch();

  // Helper function to check if object has required keys
  const hasKeys = useCallback((obj: any, ...keys: string[]) => {
    return keys.every((key) => key in obj);
  }, []);

  // Main calendar parameter processing logic
  const processCalendarParams = useCallback(() => {
    try {
      const params = queryString.parse(searchParams) as CalendarParams;
      
      // Check if URL has calendar parameters and required keys exist
      if (
        Object.keys(params).length === 0 ||
        !hasKeys(params, 'dateTo', 'dateFrom', 'timeSet')
      ) {
        return null;
      }

      // Parse dates from URL parameters
      const _startDate = new Date(params.dateFrom as string);
      const _endDate = new Date(params.dateTo as string);

      // Validate parsed dates
      if (`${_startDate} ${_endDate}`.toLowerCase()?.includes('invalid')) {
        console.warn('Invalid date parameters in URL:', params);
        return null;
      }

      // Compare with current selected dates using Luxon
      const startDateMatches = 
        luxon.DateTime.fromJSDate(_startDate).toMillis() ===
        luxon.DateTime.fromJSDate(selectedDate[0]).toMillis();
        
      const endDateMatches = 
        luxon.DateTime.fromJSDate(_endDate).toMillis() ===
        luxon.DateTime.fromJSDate(selectedDate[1]).toMillis();

      // Skip if dates are already the same as current selection
      if (startDateMatches && endDateMatches) {
        return null;
      }

      // Dispatch calendar update action
      dispatch(
        calendarAction.setHiveDate({
          timeSet: params.timeSet,
          date: [_startDate, _endDate],
          setQueryParams: false,
        })
      );

      return params;
    } catch (error) {
      console.error('Error processing calendar parameters:', error);
      return null;
    }
  }, [searchParams, selectedDate, calendarAction, dispatch, hasKeys]);

  // Auto-process calendar parameters on mount if enabled
  useEffect(() => {
    if (enabled) {
      processCalendarParams();
    }
  }, [enabled, processCalendarParams]);

  return {
    processCalendarParams,
    isProcessing: false, // Could be enhanced with loading state if needed
    lastProcessedParams: null, // Could be enhanced to track last processed params
  };
} 